
class BaseMedia extends HTMLElement {
  constructor() {
    super();
    this._abortController = new AbortController();
  }
  connectedCallback() {}
  disconnectedCallback() {
    this._abortController.abort();
  }
  play() {
    this._playerTarget().then((target) => {
      this._playerHandler(target, "play");
    });
  }
  pause() {
    this._playerTarget().then((target) => {
      this._playerHandler(target, "pause");
    });
  }
  _playerTarget() { return Promise.resolve(null); }
  _playerHandler(target, prop) {
    if (target && typeof target[prop] === "function") target[prop]();
  }
}

var onYouTubePromise = new Promise((resolve) => {
  window.onYouTubeIframeAPIReady = () => resolve();
});

var VideoMedia = class extends BaseMedia {
  connectedCallback() {
    super.connectedCallback();

    if (!this.hasAttribute("autoplay")) {
      const onHostClickOnce = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.play();
      };
      this.addEventListener("click", onHostClickOnce, {
        once: true,
        capture: true,
        signal: this._abortController.signal,
      });
    }

    if (this.hasAttribute("show-play-button") && !this.hasAttribute("autoplay") && !this.shadowRoot) {
      this.attachShadow({ mode: "open" }).appendChild(
        document.createRange().createContextualFragment(`
          <slot></slot>
          <svg part="play-button" fill="none" width="48" height="48" viewBox="0 0 48 48">
            <path d="M48 24C48 10.745 37.255 0 24 0S0 10.745 0 24s10.745 24 24 24 24-10.745 24-24Z" fill="#ffffff"/>
            <path d="M18.578 32.629a.375.375 0 0 1-.578-.316V15.687c0-.297.328-.476.578-.316l12.931 8.314c.23.147.23.483 0 .63L18.578 32.63Z" fill="#1a1a1a"/>
            <path d="M24 .5C36.979.5 47.5 11.021 47.5 24S36.979 47.5 24 47.5.5 36.979.5 24 11.021.5 24 .5Z" stroke="#1a1a1a" stroke-opacity=".12"/>
          </svg>
        `)
      );
    }
  }

  play({ restart = false } = {}) {
    if (restart && !this.hasAttribute("host")) {
      const v = this.querySelector("video");
      if (v) v.currentTime = 0;
    }
    super.play();
  }

  _playerTarget() {
    if (this.hasAttribute("host")) {
      this.setAttribute("loaded", "");
      return new Promise(async (resolve) => {
        const templateElement = this.querySelector("template");
        if (templateElement) {
          templateElement.replaceWith(
            templateElement.content.firstElementChild.cloneNode(true)
          );
        }
        const muteVideo = this.hasAttribute("autoplay") || window.matchMedia("screen and (max-width: 999px)").matches;
        const script = document.createElement("script");
        script.type = "text/javascript";

        if (this.getAttribute("host") === "youtube") {
          if (!window.YT || !window.YT.Player) {
            script.src = "https://www.youtube.com/iframe_api";
            document.head.appendChild(script);
            await new Promise((resolve2) => { script.onload = resolve2; });
          }
          await onYouTubePromise;
          this.setAttribute("can-play", "");
          const player = new YT.Player(this.querySelector("iframe"), {
            events: {
              onReady: () => {
                if (muteVideo) player.mute();
                resolve(player);
              },
              onStateChange: (event) => {
                if (event.data === YT.PlayerState.PLAYING) this.setAttribute("playing", "");
                else if (event.data === YT.PlayerState.ENDED || event.data === YT.PlayerState.PAUSED) this.removeAttribute("playing");
              },
            },
          });
        }

        if (this.getAttribute("host") === "vimeo") {
          if (!window.Vimeo || !window.Vimeo.Player) {
            script.src = "https://player.vimeo.com/api/player.js";
            document.head.appendChild(script);
            await new Promise((resolve2) => { script.onload = resolve2; });
          }
          const player = new Vimeo.Player(this.querySelector("iframe"));
          if (muteVideo) player.setMuted(true);
          this.setAttribute("can-play", "");
          player.on("play", () => this.setAttribute("playing", ""));
          player.on("pause", () => this.removeAttribute("playing"));
          player.on("ended", () => this.removeAttribute("playing"));
          resolve(player);
        }
      });
    } else {
      const videoElement = this.querySelector("video");
      this.setAttribute("loaded", "");
      this.setAttribute("can-play", "");

      if (this.hasAttribute("autoplay") && videoElement) {
        setTimeout(() => {
          videoElement.play().catch(() => {});
        }, 100);
      }

      if (videoElement) {
        videoElement.addEventListener("play", () => {
          this.setAttribute("playing", "");
          this.removeAttribute("suspended");
        });
        videoElement.addEventListener("pause", () => {
          if (!videoElement.seeking && videoElement.paused) this.removeAttribute("playing");
        });
      }
      return Promise.resolve(videoElement);
    }
  }

  _playerHandler(target, prop) {
    if (this.getAttribute("host") === "youtube") {
      prop === "play" ? target.playVideo() : target.pauseVideo();
    } else {
      if (prop === "play" && !this.hasAttribute("host")) {
        target.play().catch((error) => {
          if (error && error.name === "NotAllowedError") {
            this.setAttribute("suspended", "");
            if (!this.hasAttribute("autoplay")) {
              target.controls = true;
              const img = target.previousElementSibling?.currentSrc;
              if (img) target.poster = img;
            } else {
              const oncePlay = () => {
                document.removeEventListener("pointerdown", oncePlay, true);
                document.removeEventListener("keydown", oncePlay, true);
                target.play().catch(()=>{});
              };
              document.addEventListener("pointerdown", oncePlay, true);
              document.addEventListener("keydown", oncePlay, true);
            }
          }
        });
      } else {
        if (typeof target[prop] === "function") target[prop]();
      }
    }
  }
};


if (!window.customElements.get("video-media")) {
  window.customElements.define("video-media", VideoMedia);
}
